/**
 * Hook useProductsQuery
 *
 * Busca produtos com filtros e paginação via React Query.
 * useQuery e useInfiniteQuery ficam em hooks separados para não
 * compartilhar observer/cache incorretamente.
 */

import { useEffect } from 'react';
import { keepPreviousData, useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { getProducts } from '../services/api';
import { handleError } from '../utils/errorHandler';

const EMPTY_PRODUCTS = [];

function buildFilters(params = {}) {
  const {
    page = 1,
    limit = 12,
    brand = '',
    category = '',
    minPrice = '',
    maxPrice = '',
    sortBy = 'createdAt',
    sortOrder = 'desc',
    tvModel = '',
    boardCode = '',
    search = '',
  } = params;

  return {
    page,
    limit,
    brand,
    category,
    minPrice,
    maxPrice,
    sortBy,
    sortOrder,
    tvModel,
    boardCode,
    search,
  };
}

async function fetchProductsPage(filters) {
  const result = await getProducts(filters);
  return {
    products: Array.isArray(result?.products) ? result.products : [],
    pagination: result?.pagination || null,
  };
}

/**
 * Listagem paginada (catálogo).
 */
export function useProductsQuery(params = {}, options = {}) {
  const {
    enabled = true,
    staleTime = 2 * 60 * 1000,
    ...queryOptions
  } = options;

  const filters = buildFilters(params);

  const query = useQuery({
    queryKey: ['products', 'list', filters],
    queryFn: () => fetchProductsPage(filters),
    enabled: Boolean(enabled),
    staleTime,
    retry: 2,
    placeholderData: keepPreviousData,
    ...queryOptions,
  });

  useEffect(() => {
    if (!query.error) return;
    handleError(query.error, {
      context: 'useProductsQuery',
      showToast: false,
    });
  }, [query.error]);

  const products = Array.isArray(query.data?.products)
    ? query.data.products
    : EMPTY_PRODUCTS;

  return {
    products,
    pagination: query.data?.pagination || null,
    loading: query.isLoading,
    error: query.error,
    hasMore: query.data?.pagination?.hasNextPage || false,
    refetch: query.refetch,
    fetchNextPage: undefined,
    isFetchingNextPage: false,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    isPending: query.isPending,
    data: query.data,
  };
}

/**
 * Scroll infinito (opcional — chave de cache distinta da listagem).
 */
export function useInfiniteProductsQuery(params = {}, options = {}) {
  const {
    enabled = true,
    staleTime = 2 * 60 * 1000,
    ...queryOptions
  } = options;

  const full = buildFilters({ ...params, page: 1 });
  const filters = {
    limit: full.limit,
    brand: full.brand,
    category: full.category,
    minPrice: full.minPrice,
    maxPrice: full.maxPrice,
    sortBy: full.sortBy,
    sortOrder: full.sortOrder,
    tvModel: full.tvModel,
    boardCode: full.boardCode,
    search: full.search,
  };

  const infiniteQuery = useInfiniteQuery({
    queryKey: ['products', 'infinite', filters],
    queryFn: async ({ pageParam = 1 }) => {
      const result = await fetchProductsPage({ ...filters, page: pageParam });
      return {
        ...result,
        nextPage: result.pagination?.hasNextPage ? pageParam + 1 : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: Boolean(enabled),
    staleTime,
    retry: 2,
    initialPageParam: 1,
    ...queryOptions,
  });

  useEffect(() => {
    if (!infiniteQuery.error) return;
    handleError(infiniteQuery.error, {
      context: 'useInfiniteProductsQuery',
      showToast: false,
    });
  }, [infiniteQuery.error]);

  return {
    products:
      infiniteQuery.data?.pages.flatMap((p) => p.products || []) || [],
    loading: infiniteQuery.isLoading,
    error: infiniteQuery.error,
    hasMore: !!infiniteQuery.hasNextPage,
    fetchNextPage: infiniteQuery.fetchNextPage,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    pagination:
      infiniteQuery.data?.pages[infiniteQuery.data.pages.length - 1]
        ?.pagination || null,
    refetch: infiniteQuery.refetch,
    isLoading: infiniteQuery.isLoading,
    isFetching: infiniteQuery.isFetching,
    isError: infiniteQuery.isError,
    isPending: infiniteQuery.isPending,
    data: infiniteQuery.data,
  };
}

export default useProductsQuery;
